import {
  agendaSchema,
  audienceLabel,
  registrationViewSchema,
  type Agenda,
  type AgendaActivity,
  type EditionSummary,
  type RegistrationPerson,
} from '@koderek/shared';
import {
  ActionIcon,
  Alert,
  Anchor,
  Avatar,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { apiGet } from '../api/client';
import { ErrorAlert } from '../components/ErrorAlert';
import { IconCheck, IconMapPin, IconTrash } from '../components/icons';
import { useMe } from '../features/auth/session';
import { readActivePerson, saveActivePerson } from '../features/editions/activePerson';
import { FamilyTimeline } from '../features/family/FamilyTimeline';
import { TimelinePreview } from '../features/family/TimelinePreview';
import { dayAndMonth, formatDate, formatTime } from '../features/editions/format';
import { UpcomingEditionPicker } from '../features/editions/UpcomingEditionPicker';
import { rejectedReason } from '../features/enrollment/reasons';
import {
  useAvailability,
  useEnrollment,
  useFamilyEnrollments,
  withAvailability,
  type EnrollmentControls,
  type FamilyEnrollments,
} from '../features/enrollment/useEnrollment';
import { ALL_PEOPLE, PersonTabs } from '../features/participants/PersonTabs';
import { SendPlanButton } from '../features/plan/SendPlanButton';
import { t } from '../texts/pl';
import classes from './FamilyActivitiesPage.module.css';

const f = t.familyActivities;

type FamilyView = 'timeline' | 'list';
const VIEW_KEY = 'koderek.familyView';

/** Zapamiętany widok zakładki „Wszyscy” – tylko wygoda na tym urządzeniu. Na start lista. */
function readView(): FamilyView {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'timeline' ? 'timeline' : 'list';
  } catch {
    return 'list';
  }
}

function saveView(view: FamilyView): void {
  try {
    window.localStorage.setItem(VIEW_KEY, view);
  } catch {
    // Prywatne okno albo zablokowany magazyn – widok po prostu nie zostanie zapamiętany.
  }
}

/**
 * US-408 AC-2, US-503: zajęcia, na które zapisane są osoby z rodziny – zakładka „Wszyscy” (wszystkie zajęcia rodziny
 * z osobami) albo jedna osoba; wypis zawsze z pytaniem (Z-501). Zapis na nowe zajęcia zostaje w agendzie edycji.
 */
export function FamilyActivitiesPage() {
  return (
    <Stack gap="md" maw={880}>
      <Stack gap={4}>
        <Title order={1}>{f.title}</Title>
        <Text c="dimmed">{f.intro}</Text>
      </Stack>
      <UpcomingEditionPicker onlyRegistered empty={f.noEnrollments}>
        {(edition) => <EditionActivities key={edition.slug} edition={edition} />}
      </UpcomingEditionPicker>
    </Stack>
  );
}

function EditionActivities({ edition }: { edition: EditionSummary }) {
  const slug = edition.slug;
  const me = useMe().data;
  // Te same klucze co na stronie edycji – przejście między ekranami korzysta z pamięci podręcznej.
  const agenda = useQuery({
    queryKey: ['agenda', slug, me?.id ?? null],
    queryFn: () => apiGet(`/editions/${encodeURIComponent(slug)}/agenda`, agendaSchema),
  });
  const availability = useAvailability(slug);
  const registration = useQuery({
    queryKey: ['registration', slug],
    queryFn: () => apiGet(`/editions/${encodeURIComponent(slug)}/registration`, registrationViewSchema),
  });
  const people = registration.data?.people ?? [];
  const [chosen, setChosen] = useState(() => readActivePerson(slug));
  const showAll = chosen === ALL_PEOPLE;
  const [view, setView] = useState<FamilyView>(readView);
  const changeView = (next: FamilyView) => {
    setView(next);
    saveView(next);
  };
  const active = showAll ? undefined : (people.find((p) => p.participantId === chosen) ?? people.find((p) => !p.isGuardian) ?? people[0]);
  const version = availability.data?.version;
  const { controls, eligibility } = useEnrollment(
    slug,
    active ? `/editions/${encodeURIComponent(slug)}/participants/${active.participantId}` : null,
    active?.firstName ?? '',
    version,
  );
  const family = useFamilyEnrollments(
    slug,
    people.map((p) => p.participantId),
    version,
    showAll,
  );

  const error = agenda.error ?? registration.error ?? eligibility.error;
  if (error) return <ErrorAlert error={error} />;
  if (!agenda.data || !registration.data) return <Loader size="sm" aria-label="Ładowanie" />;

  return (
    <Stack gap="md">
      <PersonTabs
        people={people}
        value={showAll ? ALL_PEOPLE : active?.participantId}
        allLabel={f.all}
        onChange={(id) => {
          setChosen(id);
          saveActivePerson(slug, id);
        }}
      />
      {showAll ? (
        <SegmentedControl
          color="brand"
          radius="md"
          aria-label={f.view}
          value={view}
          onChange={(value) => changeView(value === 'timeline' ? 'timeline' : 'list')}
          data={[
            { value: 'list', label: f.viewList },
            { value: 'timeline', label: f.viewTimeline },
          ]}
          style={{ alignSelf: 'flex-start' }}
        />
      ) : null}
      {showAll && view === 'list' ? (
        // Plan dnia jest mniej oczywisty niż lista – zachęta, żeby go zobaczyć.
        <Paper withBorder radius="lg" p="md" bg="var(--mantine-color-brand-0)" style={{ borderColor: 'var(--mantine-color-brand-2)' }}>
          <Group gap="lg" wrap="wrap">
            <Paper radius="md" p={6} bg="var(--mantine-color-white)" style={{ cursor: 'pointer', maxWidth: '100%' }} onClick={() => changeView('timeline')}>
              <TimelinePreview />
            </Paper>
            <Stack gap="sm" align="flex-start" style={{ flex: '1 1 220px' }}>
              <Text size="sm">{f.timelinePromo}</Text>
              <Button size="sm" color="brand" onClick={() => changeView('timeline')}>
                {f.timelinePromoButton}
              </Button>
            </Stack>
          </Group>
        </Paper>
      ) : null}
      {showAll ? (
        family.items ? (
          view === 'timeline' ? (
            <FamilyTimeline
              slug={slug}
              agenda={{ ...agenda.data, activities: withAvailability(agenda.data.activities, availability.data) }}
              people={people}
              family={{ ...family, items: family.items }}
            />
          ) : (
            <FamilyCard edition={edition} agenda={agenda.data} people={people} family={family} />
          )
        ) : family.error ? (
          <ErrorAlert error={family.error} />
        ) : (
          <Loader size="sm" aria-label="Ładowanie" />
        )
      ) : controls ? (
        <PersonCard edition={edition} agenda={agenda.data} controls={controls} />
      ) : (
        <Loader size="sm" aria-label="Ładowanie" />
      )}
      {/* US-703: plan całej rodziny mailem (wcześniej na stronie edycji). */}
      <SendPlanButton path={`/editions/${encodeURIComponent(slug)}/registration`} />
      <Anchor component={Link} to={`/edycje/${slug}`} size="sm">
        {f.toAgenda}
      </Anchor>
    </Stack>
  );
}

/** Karta dnia: kafelek daty, dzień tygodnia, miejsce i liczba zajęć; w środku wiersze zajęć. */
function DayCard({ edition, count, children }: { edition: EditionSummary; count: number; children: ReactNode }) {
  const { day, month } = dayAndMonth(edition.date);
  const weekday = formatDate(edition.date).split(',')[0] ?? '';
  return (
    <Paper withBorder radius="lg" className={classes.card}>
      <Group justify="space-between" wrap="nowrap" gap="md" pb="sm">
        <Group gap="md" wrap="nowrap" miw={0}>
          <div className={classes.dateTile} title={formatDate(edition.date)}>
            <span className={classes.day}>{day}</span>
            <span className={classes.month}>{month.slice(0, 3)}</span>
          </div>
          <Stack gap={0} miw={0}>
            <Text fw={700} tt="capitalize">
              {weekday}
            </Text>
            <Text size="sm" c="dimmed">
              {edition.address} · {edition.city}
            </Text>
          </Stack>
        </Group>
        <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {f.count(count)}
        </Text>
      </Group>
      {children}
    </Paper>
  );
}

function EmptyRow({ edition, text }: { edition: EditionSummary; text: string }) {
  return (
    <Stack gap="xs" className={classes.row} align="flex-start">
      <Text c="dimmed">{text}</Text>
      <Button component={Link} to={`/edycje/${edition.slug}`} variant="light" size="sm">
        {f.toAgenda}
      </Button>
    </Stack>
  );
}

function ErrorRow({ error }: { error: unknown }) {
  const rejected = rejectedReason(error);
  if (rejected) {
    return (
      <Alert color="orange" role="alert" mb="sm">
        {rejected}
      </Alert>
    );
  }
  return error ? <ErrorAlert error={error} /> : null;
}

const byStart = (a: AgendaActivity, b: AgendaActivity) => a.startsAt.localeCompare(b.startsAt) || a.title.localeCompare(b.title, 'pl');

/** Jedna osoba: jej zajęcia po godzinie, „Zapisane” i „Wypisz”. */
function PersonCard({ edition, agenda, controls }: { edition: EditionSummary; agenda: Agenda; controls: EnrollmentControls }) {
  const enrolled = agenda.activities.filter((a) => controls.items.get(a.id)?.state === 'enrolled').sort(byStart);
  return (
    <DayCard edition={edition} count={enrolled.length}>
      <ErrorRow error={controls.error} />
      {enrolled.length ? (
        enrolled.map((a) => {
          const item = controls.items.get(a.id);
          return (
            <ActivityRow key={a.id} activity={a} agenda={agenda} slug={edition.slug} byStaff={!!item?.byStaff}>
              <Group gap="sm" wrap="nowrap" ml="auto">
                <Group gap={4} wrap="nowrap" className={classes.enrolled}>
                  <IconCheck size={16} />
                  <Text size="sm" fw={600}>
                    {f.enrolled}
                  </Text>
                </Group>
                {item?.canUnenroll ? (
                  <UnenrollConfirm name={controls.personName} activity={a} onConfirm={() => controls.unenroll(a)}>
                    {(open) => (
                      <Button variant="default" size="sm" loading={controls.pendingActivityId === a.id} onClick={open}>
                        {t.enrollment.unenroll}
                      </Button>
                    )}
                  </UnenrollConfirm>
                ) : (
                  <Text size="xs" c="dimmed" maw={160}>
                    {t.enrollment.unenrollStarted}
                  </Text>
                )}
              </Group>
            </ActivityRow>
          );
        })
      ) : (
        <EmptyRow edition={edition} text={f.empty(controls.personName)} />
      )}
    </DayCard>
  );
}

/** „Wszyscy”: zajęcia, na które zapisana jest choć jedna osoba, po godzinie – w wierszu osoby zapisane, każda z wypisem. */
function FamilyCard({
  edition,
  agenda,
  people,
  family,
}: {
  edition: EditionSummary;
  agenda: Agenda;
  people: RegistrationPerson[];
  family: FamilyEnrollments;
}) {
  const items = family.items!;
  const rows = agenda.activities
    .map((a) => ({ activity: a, people: people.filter((p) => items.get(p.participantId)?.get(a.id)?.state === 'enrolled') }))
    .filter((row) => row.people.length)
    .sort((x, y) => byStart(x.activity, y.activity));

  return (
    <DayCard edition={edition} count={rows.length}>
      <ErrorRow error={family.error} />
      {rows.length ? (
        rows.map(({ activity: a, people: enrolled }) => (
          <ActivityRow key={a.id} activity={a} agenda={agenda} slug={edition.slug}>
            <Group gap={6} wrap="wrap" ml="auto" justify="flex-end">
              {enrolled.map((p) => {
                const item = items.get(p.participantId)?.get(a.id);
                const pending = family.pending?.participantId === p.participantId && family.pending.activityId === a.id;
                return (
                  <Group key={p.participantId} gap={4} wrap="nowrap" className={classes.person}>
                    <Avatar size={22} radius="xl" color={p.isGuardian ? 'brand' : 'orange'}>
                      {p.firstName.slice(0, 1).toUpperCase()}
                    </Avatar>
                    <Text size="sm" fw={600}>
                      {p.firstName}
                    </Text>
                    {item?.canUnenroll ? (
                      <UnenrollConfirm name={p.firstName} activity={a} onConfirm={() => family.unenroll(p.participantId, a)}>
                        {(open) => (
                          <Tooltip label={f.unenrollPerson(p.firstName)}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              size="sm"
                              loading={pending}
                              aria-label={f.unenrollPersonFrom(p.firstName, a.title)}
                              onClick={open}
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </UnenrollConfirm>
                    ) : null}
                  </Group>
                );
              })}
            </Group>
          </ActivityRow>
        ))
      ) : (
        <EmptyRow edition={edition} text={f.emptyAll} />
      )}
    </DayCard>
  );
}

/** Wiersz zajęć: godzina w kolorze rodzaju, tytuł (link do szczegółów), godziny, sala i wiek; po prawej `children`. */
function ActivityRow({
  activity: a,
  agenda,
  slug,
  byStaff = false,
  children,
}: {
  activity: AgendaActivity;
  agenda: Agenda;
  slug: string;
  byStaff?: boolean;
  children: ReactNode;
}) {
  const room = agenda.rooms.find((r) => r.id === a.roomId)?.name;
  const color = agenda.types.find((ty) => ty.id === a.typeId)?.color;
  const details = [audienceLabel(a), byStaff ? t.enrollment.byStaff : null].filter(Boolean);
  return (
    <Group className={classes.row} justify="space-between" gap="sm" wrap="wrap">
      <Group gap="md" wrap="nowrap" miw={0} style={{ flex: '1 1 260px' }}>
        <div className={classes.time} style={{ '--type-color': color ?? 'var(--mantine-color-brand-6)' } as CSSProperties}>
          {formatTime(a.startsAt)}
        </div>
        <Stack gap={2} miw={0}>
          <Anchor component={Link} to={`/edycje/${slug}/zajecia/${encodeURIComponent(a.symbol)}`} c="inherit" fw={600} lineClamp={2}>
            {a.title}
          </Anchor>
          {/* Sala wyróżniona – rodzina szuka jej w budynku w dniu wydarzenia. */}
          <Group gap={8} wrap="wrap">
            {room ? (
              <span className={classes.room}>
                <IconMapPin size={16} style={{ flex: 'none' }} />
                {room}
              </span>
            ) : null}
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              {formatTime(a.startsAt)}–{formatTime(a.endsAt)}
            </Text>
            {details.length ? (
              <Text size="xs" c="dimmed" truncate>
                {details.join(' · ')}
              </Text>
            ) : null}
          </Group>
        </Stack>
      </Group>
      {children}
    </Group>
  );
}

/** Z-501: wypis zawsze z pytaniem. */
function UnenrollConfirm({
  name,
  activity,
  onConfirm,
  children,
}: {
  name: string;
  activity: AgendaActivity;
  onConfirm: () => void;
  children: (open: () => void) => ReactNode;
}) {
  const [opened, setOpened] = useState(false);
  return (
    <>
      {children(() => setOpened(true))}
      <Modal opened={opened} onClose={() => setOpened(false)} title={t.enrollment.unenrollTitle}>
        <Stack gap="sm">
          <Text>{t.enrollment.unenrollQuestion(name, `${activity.symbol} ${activity.title}`)}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpened(false)}>
              {t.registration.cancel}
            </Button>
            <Button
              color="red"
              onClick={() => {
                setOpened(false);
                onConfirm();
              }}
            >
              {t.enrollment.unenrollConfirm}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
